import React, { VFC, useEffect, useCallback } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import Box from "../../particles/Box";
import { FinnBoxStyled } from './styled';
import { t } from 'i18next';
import Text from '../../atoms/Text';
import TextInput from '../../organisms/TextInput';
import Button from '../../atoms/Button';
import IconBadge from '../../molecules/IconBadge';
import Loader from '../../atoms/Loader';
import { BoxBorderedStyled } from '../../../pages/Connections/styled';

interface ConnectFinnSectionProps {
  finnEnabled: boolean;
  finnConnecting: boolean;
  finnStatus: { id?: number; remote_id?: string, fetching_enabled?: boolean } | null;
  connectFinn: (finnAccount: { id: string; token: string }) => void;
}

const ConnectFinnSection: VFC<ConnectFinnSectionProps> = ({
  finnEnabled,
  finnConnecting,
  finnStatus,
  connectFinn
}) => {
  const isConnected = !!finnStatus?.id;

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      finn_id: finnStatus?.remote_id ?? "",
      finn_token: ""
    }
  });

  const { control, handleSubmit, setValue, reset, formState } = formMethods;
  const { isValid } = formState;

  const finnToken = useWatch({ name: "finn_token", control }) ?? "";

  const finnTokenModified = !!finnToken;
  const submitDisabled = finnConnecting || !isValid || (isConnected && !finnTokenModified);

  useEffect(() => {
    if (finnStatus?.remote_id) {
      setValue("finn_id", finnStatus.remote_id);
    }
  }, [finnStatus?.remote_id, setValue]);

  const getSubmitBtnText = () => {
    if (isConnected) {
      if (finnTokenModified) {
        return t("general.connections_finn.cta_btn.update");
      }
      return t("general.connections_finn.cta_btn.connected");
    }
    return t("general.connections_finn.cta_btn.connect");
  };

  const onSubmit = useCallback(
    async (data) => {
      if (finnConnecting) return;

      const finnAccount = { id: data.finn_id, token: data.finn_token };

      connectFinn(finnAccount);

      reset({ finn_id: data.finn_id, finn_token: "" });
    },
    [finnConnecting, isConnected, connectFinn, reset]
  );

  if (!finnEnabled) {
    return null;
  }

  return (
    <Box marginT={4.5} marginB={3} marginH={1}>
      <BoxBorderedStyled>
        <FinnBoxStyled>
          <Box marginB={3} textCenter>
            <IconBadge name="finn" />
          </Box>
          <Box marginB={4} textCenter>
            <Text as="h2" styledAs="h1" responsive>
              FINN.no
            </Text>
          </Box>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box marginH={4} marginB={4}>
                <Box marginB={3}>
                  <TextInput
                    name="finn_id"
                    label={t("general.connections_finn.id_label")}
                    required
                  />
                </Box>
                <Box marginB={4}>
                  <TextInput
                    name="finn_token"
                    label={t("general.connections_finn.token_label")}
                    required
                  />
                </Box>
                <Box flex justifyCenter>
                  <Loader active={finnConnecting}>
                    <Button
                      cta
                      type="submit"
                      disabled={submitDisabled}
                    >
                      {getSubmitBtnText()}
                    </Button>
                  </Loader>
                </Box>
              </Box>
            </form>
          </FormProvider>
        </FinnBoxStyled>
      </BoxBorderedStyled>
    </Box>
  );
};

export default ConnectFinnSection;
